import { z } from "zod";
import { Rng } from "../rng.js";
import type { GameModule, GeneratedVariant, Hint, SettingsField, ValidationResult } from "../types.js";

// Первая строка: робот просит ответить ему кодом. Блоки с полки складываются
// в строку, кнопка «Запустить» её проверяет. Проверяет сервер: у него эталон,
// у клиента — только перемешанные блоки.

export const TOKEN_KINDS = ["kw", "fn", "name", "op", "punct", "str", "num"] as const;
export type TokenKind = (typeof TOKEN_KINDS)[number];

const tokenSchema = z.object({
  text: z.string().min(1).max(120),
  kind: z.enum(TOKEN_KINDS).default("name"),
});

const lineSchema = z.object({
  text: z.string().min(1).max(500),
  /** По этой реплике проносится вирус — вспышка воспоминания */
  virus: z.boolean().default(false),
});

export const codeLineSettingsSchema = z.object({
  characterName: z.string().max(40).default("Бип"),
  characterSprite: z.string().max(120).default("/assets/robot.webp"),
  villainSprite: z.string().max(120).default("/assets/virus_run.webp"),
  backdrop: z.string().max(120).default("/assets/city.webp"),
  fileName: z.string().max(40).default("answer.js"),

  task: z
    .string()
    .max(500)
    .default(
      "Собрать две команды: положить ответ в переменную text и показать её командой alert. В дело идут все блоки.",
    ),

  /** Реплики до начала задания */
  introLines: z
    .array(z.string().max(500))
    .default([
      "…Привет.",
      "Спасибо за помощь. Я думал, это мой конец.",
      "Ты меня понимаешь? Голосом не отвечай — слух он мне тоже попортил.",
      "Собери ответ из блоков: положи его в переменную и покажи командой alert.",
    ]),

  /** Реплики после верного ответа */
  storyLines: z
    .array(lineSchema)
    .default([
      { text: "Ты ответил. Значит, ты и правда говоришь на моём языке.", virus: false },
      { text: "Меня зовут Бип. Я смотрю за этим кварталом… смотрел.", virus: false },
      { text: "Три дня назад в город попал вирус.", virus: true },
      { text: "Сначала погасли вывески. Потом лифты, светофоры, двери.", virus: false },
      { text: "Он добрался и до меня: разобрал спину и стёр половину памяти.", virus: false },
      { text: "Я отключился прямо посреди улицы. Если бы не ты — так и остался бы железом.", virus: false },
      { text: "Его нужно догнать, пока город не встал совсем.", virus: false },
      { text: "Идём. Я покажу, с чего всё началось.", virus: false },
    ]),

  /** Блоки решения по порядку */
  tokens: z
    .array(tokenSchema)
    .min(2)
    .default([
      { text: "let", kind: "kw" },
      { text: "text", kind: "name" },
      { text: "=", kind: "op" },
      { text: "'Привет, что у вас случилось?'", kind: "str" },
      { text: ";", kind: "punct" },
      { text: "alert", kind: "fn" },
      { text: "(", kind: "punct" },
      { text: "text", kind: "name" },
      { text: ")", kind: "punct" },
      { text: ";", kind: "punct" },
    ]),

  /** Подсказка на каждую позицию строки: чем объяснить ошибку */
  hints: z
    .array(z.string().max(300))
    .default([
      "Переменную сначала объявляют словом let.",
      "После let идёт имя переменной — text.",
      "Значение переменной присваивают знаком =.",
      "Справа от = должен стоять ответ — текст в кавычках.",
      "Команда закончилась, её закрывают точкой с запятой.",
      "Показать текст умеет команда alert.",
      "После alert открывают круглую скобку.",
      "В скобках пишут, что показать, — переменную text.",
      "Открытую скобку нужно закрыть.",
      "Вторая команда тоже кончается точкой с запятой.",
    ]),

  /** Лишние блоки на полке: в решение не входят, но сбивают с толку */
  distractors: z.array(tokenSchema).default([]),

  emptyHint: z.string().max(300).default("Перенесите блоки с полки в строку — по порядку."),
  extraHint: z.string().max(300).default("В конце лишний блок — уберите его."),
  errorReply: z.string().max(300).default("Так не выйдет. Посмотри, что пишет консоль."),
  waitReply: z.string().max(300).default("Жду ответа. Блоки внизу — все до одного."),
  shuffleBlocks: z.boolean().default(true),
});

export type CodeLineSettings = z.infer<typeof codeLineSettingsSchema>;

export const codeLinePayloadSchema = z.object({
  characterName: z.string(),
  characterSprite: z.string(),
  villainSprite: z.string(),
  backdrop: z.string(),
  fileName: z.string(),
  task: z.string(),
  introLines: z.array(z.string()),
  storyLines: z.array(z.object({ text: z.string(), virus: z.boolean() })),
  /** Блоки в порядке полки: решение вперемешку с лишними */
  blocks: z.array(z.object({ id: z.string(), text: z.string(), kind: z.enum(TOKEN_KINDS) })),
  /** Что покажет окно alert, когда код заработает */
  answerText: z.string(),
  waitReply: z.string(),
  errorReply: z.string(),
});

export type CodeLinePayload = z.infer<typeof codeLinePayloadSchema>;

export const codeLineSolutionSchema = z.object({
  code: z.array(z.string().max(120)).max(64),
});

export type CodeLineSolution = z.infer<typeof codeLineSolutionSchema>;

const FIELDS: SettingsField[] = [
  { path: "characterName", label: "Имя персонажа", kind: "text", group: "Персонаж" },
  {
    path: "characterSprite",
    label: "Картинка персонажа",
    kind: "text",
    hint: "Путь вида /assets/robot.webp или /media/<файл> из медиатеки",
    group: "Персонаж",
  },
  { path: "villainSprite", label: "Картинка вируса", kind: "text", group: "Персонаж" },
  { path: "backdrop", label: "Фон сцены", kind: "text", group: "Персонаж" },
  { path: "fileName", label: "Имя файла в редакторе", kind: "text", group: "Редактор" },
  { path: "task", label: "Текст задачи", kind: "textarea", group: "Редактор" },
  {
    path: "introLines",
    label: "Реплики до задания",
    kind: "lines",
    hint: "Каждая строка — отдельная реплика, игрок листает их касанием",
    group: "Диалог",
  },
  {
    path: "storyLines",
    label: "Реплики после победы",
    kind: "story",
    hint: "Отметьте реплику, на которой по улице проносится вирус",
    group: "Диалог",
  },
  { path: "waitReply", label: "Реплика в ожидании", kind: "text", group: "Диалог" },
  { path: "errorReply", label: "Реплика при ошибке", kind: "text", group: "Диалог" },
  {
    path: "tokens",
    label: "Блоки решения по порядку",
    kind: "tokens",
    hint: "Ровно та строка, которую должен собрать игрок",
    group: "Код",
  },
  {
    path: "hints",
    label: "Подсказки по позициям",
    kind: "lines",
    hint: "Первая строка объясняет первый блок, вторая — второй и так далее",
    group: "Код",
  },
  {
    path: "distractors",
    label: "Лишние блоки",
    kind: "tokens",
    hint: "Попадут на полку, но в решение не входят",
    group: "Код",
  },
  { path: "emptyHint", label: "Подсказка на пустую строку", kind: "text", group: "Код" },
  { path: "extraHint", label: "Подсказка на лишний блок", kind: "text", group: "Код" },
  { path: "shuffleBlocks", label: "Перемешивать полку", kind: "boolean", group: "Код" },
];

/** Короткая запись блока для сообщения об ошибке: длинные строки режем */
const show = (token: string): string =>
  token.length > 14 ? `'${token.slice(1, 12)}…'` : `'${token}'`;

/** Что показывает окно alert: содержимое первого строкового блока */
function answerOf(settings: CodeLineSettings): string {
  const str = settings.tokens.find((token) => token.kind === "str");
  if (!str) return "";
  return str.text.replace(/^['"`]|['"`]$/g, "");
}

function generate(settings: CodeLineSettings, seed: number): GeneratedVariant<CodeLinePayload, CodeLineSolution> {
  const rng = new Rng(seed);
  const all = [
    ...settings.tokens.map((token, index) => ({ id: `s${index}`, ...token })),
    ...settings.distractors.map((token, index) => ({ id: `d${index}`, ...token })),
  ];
  const blocks = settings.shuffleBlocks ? rng.shuffle(all) : all;

  return {
    payload: {
      characterName: settings.characterName,
      characterSprite: settings.characterSprite,
      villainSprite: settings.villainSprite,
      backdrop: settings.backdrop,
      fileName: settings.fileName,
      task: settings.task,
      introLines: settings.introLines,
      storyLines: settings.storyLines,
      blocks,
      answerText: answerOf(settings),
      waitReply: settings.waitReply,
      errorReply: settings.errorReply,
    },
    solution: { code: settings.tokens.map((token) => token.text) },
    metrics: {
      tokens: settings.tokens.length,
      distractors: settings.distractors.length,
      lines: settings.tokens.filter((token) => token.text === ";").length || 1,
    },
  };
}

/**
 * Разбор ошибки: ищем первое место, где набранное разошлось с эталоном,
 * и объясняем именно его. Тот же разбор, что показывался в прототипе, —
 * только теперь он живёт на сервере.
 */
export function diagnoseCode(
  code: string[],
  expected: string[],
  settings: CodeLineSettings,
): { error: string; hint: string; at: number | null } | null {
  if (!code.length) {
    return { error: "Пустая программа", hint: settings.emptyHint, at: null };
  }

  const total = Math.max(code.length, expected.length);
  for (let i = 0; i < total; i++) {
    const got = code[i];
    const want = expected[i];
    if (got === want) continue;

    if (got === undefined) {
      return {
        error: "SyntaxError: Unexpected end of input",
        hint: settings.hints[i] ?? "Строка ещё не закончена.",
        at: null,
      };
    }
    if (want === undefined) {
      return { error: `SyntaxError: Unexpected token ${show(got)}`, hint: settings.extraHint, at: i };
    }

    // имя переменной в начале строки движок JS назовёт иначе, чем синтаксис
    const isName = settings.tokens.find((token) => token.text === got)?.kind === "name";
    const error =
      i === 0 && isName
        ? `ReferenceError: ${got} is not defined`
        : `SyntaxError: Unexpected token ${show(got)}`;
    return { error, hint: settings.hints[i] ?? "Здесь должен стоять другой блок.", at: i };
  }

  return null;
}

function validate(
  payload: CodeLinePayload,
  submitted: CodeLineSolution,
  settings: CodeLineSettings,
): ValidationResult {
  const code = submitted.code ?? [];
  const known = new Set(payload.blocks.map((block) => block.text));
  if (code.some((token) => !known.has(token))) {
    return { solved: false, reason: "unknown_token", mistakes: 1 };
  }

  const expected = settings.tokens.map((token) => token.text);
  const problem = diagnoseCode(code, expected, settings);

  if (!problem) return { solved: true, mistakes: 0, details: { lines: code.length } };

  return {
    solved: false,
    reason: code.length ? "wrong_code" : "empty_code",
    mistakes: 1,
    details: { error: problem.error, hint: problem.hint, at: problem.at },
  };
}

function minDurationMs(payload: CodeLinePayload): number {
  // каждый блок нужно хотя бы донести до строки
  return Math.max(6000, payload.blocks.length * 900);
}

function hint(payload: CodeLinePayload, solution: CodeLineSolution, used: number): Hint | null {
  const code = solution.code ?? [];
  if (!code.length) return null;
  const index = Math.min(used, code.length - 1);
  const token = code[index]!;

  return {
    kind: "token_at",
    data: { index, token },
    text: `Блок №${index + 1} в строке — ${token}`,
  };
}

export const codeLine: GameModule<CodeLineSettings, CodeLinePayload, CodeLineSolution> = {
  code: "code_line",
  title: "Первая строка",
  description: "Собрать из блоков команду и запустить её",
  settingsSchema: codeLineSettingsSchema,
  payloadSchema: codeLinePayloadSchema,
  solutionSchema: codeLineSolutionSchema,
  defaultSettings: codeLineSettingsSchema.parse({}),
  settingsFields: FIELDS,
  generate,
  validate,
  minDurationMs,
  hint,
};
